import DriveView from "../../DriveView";
import { Device } from "../../model/Device";
import { getCurrentVehicle } from "../../utils/device";
import constants from "../../constants";
import Api from "../../services/api";
import { IAssignmentProximityInfo } from "../../services/vehicleProximity";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { getServerDate } from "../../utils/storage/date";
import dayjs from "../../utils/dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { handleApi } from "../../utils/error";
import { IApiError, TMultiCallParam } from "../../utils/api.interfaces";
import { WorkdayType } from "../../store/data/types";
import * as logger from "../../utils/logger";
import { IVehicleListItem } from "./helper";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import { getDeviceAssignedToDriver } from "../../sync/driverChangeHelper";
import {
    filterArchivedVehicles,
    formatNearbyVehicles,
    findLastUsedVehicleRecord,
    ILastUsedVehicleRecord
} from "../../modules/assets/helpers/vehicleSearchHelper";

const MAX_SEARCH_ASSETS = 20;
const CURRENT_VEHICLE_NOT_FOUND_MESSAGE = "Could not find current vehicle";

dayjs.extend(relativeTime);

interface ILastUsedVehicle {
    label: string;
    device: Device;
}

export class SuggestedVehicles extends DriveView<Backbone.Model> {

    protected strings = [
        "in {0}",
        "{0} away",
        "Last used"
    ];
    private readonly api: Api;
    private readonly deviceCache: BaseCache<IDevice | Device>;
    private readonly user: DeepReadonly<IUser> | null;
    private readonly currentVehicle: IIdEntity | null;
    private readonly isInitial: boolean;
    private lastUsedVehicles: IVehicleListItem[] | null = null;
    private cachedNearestVehicles: IVehicleListItem[];

    constructor(options) {
        super(options);

        this.app = options.app;
        this.deviceCache = makeDeviceCache(this.app.store);
        this.api = this.app.api;
        this.user = this.app.userService.getCurrentUser();
        this.currentVehicle = getCurrentVehicle();
        this.isInitial = options.isInitial;
    }

    findSuggestedVehicles(useCached: boolean) {
        return this.translate()
            .then(() => {
                const lastUsedPromise = this.isInitial && !this.lastUsedVehicles
                    ? this.findLastUsedVehicle()
                    : Promise.resolve();

                // The proximity API call is independent of the last-used lookup — kick it off in parallel.
                // Formatting the results (which excludes the last-used vehicle) happens after both resolve.
                // null sentinel has two meanings: cache hit (skip API) or API failure (fall back to cache/empty).
                // In both cases the handler below returns cachedNearestVehicles || [], which is the correct UX:
                // serve a stale cache when available rather than showing nothing after a transient GPS failure.
                const proximityDataPromise = useCached && this.cachedNearestVehicles
                    ? Promise.resolve(null)
                    : this.app.context.vehicleProximity.getAssignmentInfo(MAX_SEARCH_ASSETS, true, false)
                        .catch(() => null);

                return Promise.all([lastUsedPromise, proximityDataPromise]);
            })
            .then(([, proximityData]) => {
                if (proximityData === null) {
                    // Either cache path or API failure — serve cached results if available, otherwise empty.
                    return Promise.resolve(this.cachedNearestVehicles || []);
                }
                return this.processNearestVehicles(proximityData);
            })
            .then((nearestVehicles) => (this.lastUsedVehicles || []).concat(nearestVehicles))
            .then((vehicles) => filterArchivedVehicles(vehicles));
    }

    findCurrentlyAssignedVehicle(): Promise<IDevice | null> {
        return new Promise(resolve => {
            if (!this.user) {
                resolve(null);
                return;
            }

            return getDeviceAssignedToDriver(this.app.api, this.user.id)
                .then((device: IIdEntity | null) => {
                    if (device) {
                        return this.deviceCache.getOrFetch(this.app.api, [device])
                            .then(populatedDevice => {
                                resolve(populatedDevice?.length ? populatedDevice[0][0] : null);
                                return;
                            });
                    }
                    resolve(null);
                    return;
                });
        });
    }

    private findLastUsedVehicle() {
        return this.getLastUsedVehicle()
            .then(lastUsed => {
                let lastUsedVehicles: IVehicleListItem[] = [];
                if (lastUsed) {
                    let lastUsedVehicleDate = lastUsed.label;
                    let lastUsedVehicleDateLabel = lastUsedVehicleDate ? (lastUsedVehicleDate.indexOf(constants.APP_NAME) > -1 ? lastUsedVehicleDate : dayjs(lastUsedVehicleDate).fromNow()) : "";

                    lastUsedVehicles.push({
                        name: lastUsed.device.name + (lastUsed.device.licensePlate ? ` (${ lastUsed.device.licensePlate })` : ""),
                        subtitle: this.getString("Last used") + (lastUsedVehicleDateLabel ? ` ${ lastUsedVehicleDateLabel }` : ""),
                        device: lastUsed.device
                    });
                }

                this.lastUsedVehicles = lastUsedVehicles;
            });
    }

    private getCurrentVehicle() {
        return new Promise<Device[]>((resolve, reject) => {
            if (this.currentVehicle && Object.keys(this.currentVehicle).length > 0) {
                // Handle offline state for already assigned vehicle selection
                this.deviceCache.getOrFetch(this.api, { id: this.currentVehicle.id })
                    .then(results => {
                        if (!results || results.length === 0) {
                            reject(new Error(CURRENT_VEHICLE_NOT_FOUND_MESSAGE));
                            return;
                        }
                        let devicesResults = results[0];
                        resolve(devicesResults && devicesResults.map(d => new Device(d)));
                    })
                    .catch(reject);
            } else {
                reject(new Error(CURRENT_VEHICLE_NOT_FOUND_MESSAGE));
            }
        });
    }

    private validateRange(deviceId: string) {
        return this.app.context.vehicleProximity.getDeviceStatus(deviceId).then(status => {
            if (status.distance > 1) {
                // Vehicle is out of the 1km allowable range for "continue with last used..."
               throw new Error("Vehicle is out of range.");
            }

            return Promise.resolve();
        });
    }

    private getLastUsedVehicle(): Promise<ILastUsedVehicle | null> {
        return new Promise<ILastUsedVehicle | null>(resolve => {
            if (!this.currentVehicle || this.currentVehicle.id === "NoDeviceId") {
                return this.findCurrentlyAssignedVehicle()
                    .then((currentlyAssignedVehicle: IDevice | null) => {
                        if (currentlyAssignedVehicle) {
                            resolve({
                                label: "",
                                device: new Device(currentlyAssignedVehicle)
                            });
                            return;
                        }
                        return this.findLastUsedVehicleByTripOrLog().then(resolve);
                    });
            }

            return this.getCurrentVehicle()
                .then((devices: Device[]) => {
                    if (devices.length === 0) {
                        resolve(null);
                        return undefined;
                    }

                    let resolveLastUsed = () => {
                        resolve({
                            label: this.getString("in {0}").replace("{0}", constants.APP_NAME),
                            device: new Device(devices[0])
                        });
                    };

                    // If starting workday & have a current vehicle, let them choose it
                    if (this.user && this.app.store.getState().data.workday[this.user.id] === WorkdayType.Stopped) {
                        resolveLastUsed();
                        return undefined;
                    }

                    return this.validateRange(devices[0].id)
                        .then(() => {
                            resolveLastUsed();
                        })
                        .catch(e => {
                            logger.error(logger.Source.ENTITY, `validateRange problem: ${ e.message }`);
                            resolve(null);
                        });
                })
                .catch(error => {
                    if (error && error.message !== CURRENT_VEHICLE_NOT_FOUND_MESSAGE) {
                        logger.error(logger.Source.ERROR, `Error getting currentVehicle ${ error?.message }`, this.api.getUser(), error);
                    }
                    this.findLastUsedVehicleByTripOrLog().then(resolve);
                });
        });
    }

    private findLastUsedVehicleByTripOrLog() {
        return new Promise<ILastUsedVehicle | null>(resolve => {
            if (!this.user) {
                logger.error(logger.Source.ERROR, `No user found in findLastUsedVehicleByTripOrLog`);
                resolve(null);
                return;
            }

            findLastUsedVehicleRecord(this.api, this.user.id)
                .then((record: ILastUsedVehicleRecord | null) => {
                    if (!record || !record.device) {
                        return Promise.reject();
                    }
                    return this.deviceCache.getOrFetch(this.api, [record.device], (this.user as IUser).id)
                        .then(devResults => {
                            return Promise.resolve({ devResults: devResults, record: record });
                        });
                })
                .then(devResultsAndRecord => {
                    const devResults = devResultsAndRecord.devResults;
                    const record = devResultsAndRecord.record;
                    const devResult: Device | null = devResults && devResults[0] && devResults[0][0] ? new Device(devResults[0][0]) : null;

                    // Check that the device is still present and active (not archived) in the DB
                    if (!devResult || !devResult.activeTo || devResult.activeTo.getTime() < getServerDate().getTime()) {
                        return Promise.reject();
                    }

                    return this.validateRange(devResult.id)
                        .then(() => {
                            resolve({
                                label: record.dateTime ? record.dateTime : null,
                                device: devResult
                            });
                            return;
                        }).catch((e: IApiError) => {
                            logger.error(logger.Source.ENTITY, `findLastUsedVehicleByTripOrLog problem: ${ e.message }`, this.api.getUser(), e);
                            resolve(null);
                        });
                })
                .catch(() => resolve(null));
        });
    }

    private getNearbyVehicleInfo (devices: IIdEntity[], distances: IIdDictionary<number>): Promise<IVehicleListItem[]> {
        return new Promise(resolve => {
            if (devices.length && distances) {
                let multiCalls = devices.map(device => ["Get", {
                    typeName: "Device",
                    search: { id: device.id }
                }] as TMultiCallParam);

                this.api.multiCall(
                    multiCalls,
                    results => {
                        resolve(this.formatNearestVehicles([].concat.apply([], results || []), distances));
                    },
                    errorObject => {
                        handleApi(errorObject);
                        resolve([]);
                    }
                );
            } else {
                this.cachedNearestVehicles = [];
                resolve([]);
            }
        });
    }

    private formatNearestVehicles (assets: IDevice[], distances: IIdDictionary<number>): IVehicleListItem[] {
        const isMetric = this.user ? !!this.user.isMetric : true;
        const proximityString = this.getString("{0} away");

        // Build set of IDs to exclude (current vehicle and last used vehicle)
        const excludeIds = new Set<string>();
        if (this.currentVehicle?.id) {
            excludeIds.add(this.currentVehicle.id);
        }
        if (this.lastUsedVehicles?.[0]?.device?.id) {
            excludeIds.add(this.lastUsedVehicles[0].device.id);
        }

        const nearestVehicles = formatNearbyVehicles(assets, distances, isMetric, proximityString, excludeIds);
        this.cachedNearestVehicles = nearestVehicles;
        return nearestVehicles;
    }

    private processNearestVehicles(proximityData: IAssignmentProximityInfo): Promise<IVehicleListItem[]> {
        let deviceDistances: IIdDictionary<number> = {};
        let devices: IIdEntity[] = [];

        if (proximityData.statuses && proximityData.statuses.length > 0) {
            proximityData.statuses.forEach(deviceStatusInfo => {
                // `distance` is not returned by the DeviceStatusInfo API — it is computed from
                // `latitude`/`longitude` (which are included in the propertySelector) and
                // mutated onto each status object in-place by `findCloseVehicleStatuses`
                // inside `vehicleProximity.getAssignmentInfo` before the promise resolves.
                if (deviceStatusInfo.distance <= 1) {
                    deviceDistances[deviceStatusInfo.device.id] = deviceStatusInfo.distance;
                    devices.push(deviceStatusInfo.device);
                }
            });
        }

        return this.getNearbyVehicleInfo(devices, deviceDistances);
    }

}